import { useState, useMemo, useEffect } from 'react'
import { LayoutGrid, Users, Trophy, ChevronDown, Calendar } from 'lucide-react'
import { trackBracketActivity } from '../config/analytics'

function BracketView({ categories, tournament, themeClasses, isDarkMode, colorTheme }) {
  // Filter categories that have bracket data
  const categoriesWithBrackets = useMemo(() => 
    categories.filter(c => c.brackets && c.brackets.length > 0),
    [categories]
  )

  // Group categories by date and sort (matching TournamentView logic)
  const { sortedDateGroups, dateKeys } = useMemo(() => {
    const byDate = categoriesWithBrackets.reduce((groups, category) => {
      const startDate = category.startDate || tournament.startDate
      const dateKey = startDate ? new Date(startDate).toDateString() : 'No Date'
      if (!groups[dateKey]) groups[dateKey] = []
      groups[dateKey].push(category)
      return groups
    }, {})

    const sorted = Object.entries(byDate).sort((a, b) => {
      if (a[0] === 'No Date') return 1
      if (b[0] === 'No Date') return -1
      return new Date(a[0]) - new Date(b[0])
    })

    // Sort categories within each date group by title, then skill level
    sorted.forEach(([, cats]) => {
      cats.sort((a, b) => {
        const titleCompare = (a.title || '').localeCompare(b.title || '')
        if (titleCompare !== 0) return titleCompare
        if (tournament.skillLevels && tournament.skillLevels.length > 0) {
          const aIdx = tournament.skillLevels.findIndex(sl => sl.name === a.skillLevel)
          const bIdx = tournament.skillLevels.findIndex(sl => sl.name === b.skillLevel)
          if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx
          if (aIdx !== -1 && bIdx === -1) return -1
          if (aIdx === -1 && bIdx !== -1) return 1
        }
        return 0
      })
    })

    return { sortedDateGroups: sorted, dateKeys: sorted.map(([k]) => k) }
  }, [categoriesWithBrackets, tournament])

  const [selectedDay, setSelectedDay] = useState(dateKeys[0] || null)
  const hasMultipleDays = dateKeys.length > 1

  // Get categories for selected day, sorted
  const dayCategoriesWithBrackets = useMemo(() => {
    if (!selectedDay) return categoriesWithBrackets
    const group = sortedDateGroups.find(([k]) => k === selectedDay)
    return group ? group[1] : []
  }, [selectedDay, sortedDateGroups, categoriesWithBrackets])

  const [selectedCategoryId, setSelectedCategoryId] = useState(
    dayCategoriesWithBrackets.length > 0 ? dayCategoriesWithBrackets[0].id : null
  )

  // Track brackets viewed on mount
  useEffect(() => {
    trackBracketActivity('Viewed', { label: selectedDay, value: categoriesWithBrackets.length })
  }, [])

  // When day changes, auto-select first category of that day
  const handleDayChange = (day) => {
    setSelectedDay(day)
    trackBracketActivity('Day Selected', { label: formatDayLabel(day) })
    const group = sortedDateGroups.find(([k]) => k === day)
    if (group && group[1].length > 0) {
      setSelectedCategoryId(group[1][0].id)
    }
  }

  const selectedCategory = categoriesWithBrackets.find(c => c.id === selectedCategoryId)

  // Format date for day tabs
  const formatDayLabel = (dateKey) => {
    if (dateKey === 'No Date') return 'Unscheduled'
    const d = new Date(dateKey)
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
  }

  if (categoriesWithBrackets.length === 0) {
    return (
      <div className="space-y-5 animate-fade-in">
        <h2 className={`text-2xl sm:text-3xl font-black tracking-tight ${themeClasses.text}`}>Brackets</h2>
        <div className={`${themeClasses.glassDark} rounded-2xl p-10 text-center space-y-4`}>
          <div className={`inline-flex items-center justify-center w-24 h-24 ${isDarkMode ? 'bg-gradient-to-br from-white/10 to-white/5' : 'bg-gradient-to-br from-gray-100 to-gray-50'} rounded-2xl mb-4`}>
            <LayoutGrid className={`w-12 h-12 ${themeClasses.textMuted}`} />
          </div>
          <h3 className={`text-2xl font-bold ${themeClasses.text}`}>No Brackets Available</h3>
          <p className={`max-w-md mx-auto text-lg ${themeClasses.textMuted}`}>
            Brackets have not been generated yet. Check back soon!
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-5 animate-fade-in">
      <div className="flex flex-col gap-3">
        <h2 className={`text-2xl sm:text-3xl font-black tracking-tight ${themeClasses.text}`}>Brackets</h2>

        {/* Day Filter Tabs */}
        {hasMultipleDays && (
          <div className="flex flex-wrap gap-2">
            {dateKeys.map((dateKey) => {
              const isActive = dateKey === selectedDay
              return (
                <button
                  key={dateKey}
                  onClick={() => handleDayChange(dateKey)}
                  className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-semibold border transition-all duration-200 ${
                    isActive
                      ? isDarkMode
                        ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40'
                        : 'bg-emerald-50 text-emerald-700 border-emerald-300'
                      : isDarkMode
                        ? 'bg-white/[0.04] text-white/50 border-white/[0.08] hover:bg-white/[0.08] hover:text-white/70'
                        : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100 hover:text-gray-700'
                  }`}
                >
                  <Calendar className="w-3.5 h-3.5" />
                  {formatDayLabel(dateKey)}
                </button>
              )
            })}
          </div>
        )}

        {/* Category Selector */}
        {dayCategoriesWithBrackets.length > 1 && (
          <div className="relative w-fit">
            <select
              value={selectedCategoryId || ''}
              onChange={(e) => {
                setSelectedCategoryId(e.target.value)
                const cat = categoriesWithBrackets.find(c => c.id === e.target.value)
                trackBracketActivity('Category Selected', { label: cat?.title || e.target.value, skill_level: cat?.skillLevel || '' })
              }}
              className={`appearance-none pl-4 pr-10 py-2.5 rounded-xl font-semibold text-sm border transition-all duration-300 cursor-pointer ${
                isDarkMode 
                  ? 'bg-white/[0.06] border-white/[0.12] text-white hover:bg-white/[0.1]' 
                  : 'bg-white border-gray-200 text-gray-900 hover:bg-gray-50'
              } focus:outline-none focus:ring-2 focus:ring-emerald-500/50`}
            >
              {dayCategoriesWithBrackets.map(cat => (
                <option key={cat.id} value={cat.id}>
                  {cat.title}
                  {(cat.skillLevel || cat.ageGroup) ? ` (${[cat.skillLevel, cat.ageGroup].filter(Boolean).join(' • ')})` : ''}
                </option>
              ))}
            </select>
            <ChevronDown className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none ${themeClasses.textMuted}`} />
          </div>
        )}

        {/* Single category title when only one in selected day */}
        {dayCategoriesWithBrackets.length === 1 && selectedCategory && (
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-emerald-400" />
            <span className={`text-lg font-bold ${themeClasses.text}`}>
              {selectedCategory.title}
              {(selectedCategory.skillLevel || selectedCategory.ageGroup) && (
                <span className={`ml-2 text-base font-semibold ${themeClasses.textMuted}`}>
                  ({[selectedCategory.skillLevel, selectedCategory.ageGroup].filter(Boolean).join(' • ')})
                </span>
              )}
            </span>
          </div>
        )}
      </div>

      {/* Bracket Groups */}
      {selectedCategory && selectedCategory.brackets && (
        <div className="grid gap-5 sm:gap-6">
          {selectedCategory.brackets.map((bracket) => (
            <div
              key={bracket.bracketNumber}
              className={`${themeClasses.cardBg} rounded-2xl border overflow-hidden transition-all duration-300`}
            >
              {/* Group Header */}
              <div className={`px-5 py-4 border-b ${themeClasses.border} ${isDarkMode ? 'bg-white/[0.03]' : 'bg-gray-50/50'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-lg ${
                      isDarkMode 
                        ? 'bg-gradient-to-br from-emerald-500/20 to-teal-500/20 text-emerald-400 border border-emerald-500/30' 
                        : 'bg-gradient-to-br from-emerald-50 to-teal-50 text-emerald-600 border border-emerald-200'
                    }`}>
                      {bracket.bracketNumber}
                    </div>
                    <div>
                      <h3 className={`text-lg font-black ${themeClasses.text}`}>
                        Group {bracket.bracketNumber}
                      </h3>
                      <p className={`text-xs font-semibold ${themeClasses.textMuted}`}>
                        {bracket.teams.length} {bracket.teams.length === 1 ? 'team' : 'teams'}
                      </p>
                    </div>
                  </div>
                  <Users className={`w-5 h-5 ${themeClasses.textMuted}`} />
                </div>
              </div>

              {/* Teams List */}
              <div className="divide-y divide-white/[0.06]">
                {bracket.teams.map((team, index) => (
                  <div
                    key={team.id}
                    className={`px-5 py-3.5 flex items-center gap-4 transition-colors duration-200 ${
                      isDarkMode ? 'hover:bg-white/[0.03]' : 'hover:bg-gray-50'
                    }`}
                  >
                    {/* Seed / Index */}
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                      isDarkMode 
                        ? 'bg-white/[0.06] text-white/60' 
                        : 'bg-gray-100 text-gray-500'
                    }`}>
                      {team.seed || index + 1}
                    </div>

                    {/* Team Info */}
                    <div className="flex-1 min-w-0">
                      {team.teamName ? (
                        <p className={`font-bold text-sm truncate ${themeClasses.text}`}>
                          {team.teamName}
                        </p>
                      ) : (
                        <p className={`font-bold text-sm truncate ${themeClasses.textMuted}`}>
                          Team {team.seed || index + 1}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary */}
      {selectedCategory && (
        <div className={`${isDarkMode ? 'bg-white/[0.03]' : 'bg-gray-50'} rounded-xl p-4 border ${themeClasses.border}`}>
          <p className={`text-sm ${themeClasses.textMuted}`}>
            <span className="font-semibold">{selectedCategory.title}</span> — {selectedCategory.brackets.length} {selectedCategory.brackets.length === 1 ? 'group' : 'groups'}, {selectedCategory.brackets.reduce((sum, b) => sum + b.teams.length, 0)} teams total
          </p>
        </div>
      )}
    </div>
  )
}

export default BracketView
